export const BRASIL = "BRA";

export const ADDRESS_FIELD_MAP = Object.freeze({
  postalCode: "Cep",
  street: "Logradouro",
  number: "Numero",
  complement: "Complemento",
  neighborhood: "Bairro",
  city: "Cidade",
  region: "Uf",
  country: "Pais",
});

const REQUIRED_FIELDS = [
  "postalCode",
  "street",
  "number",
  "neighborhood",
  "city",
  "region",
];

const FIELD_LABELS = Object.freeze({
  street: "Endereço/Logradouro",
  number: "Número",
  neighborhood: "Bairro",
  city: "Cidade",
  region: "Estado / Província / Região",
  country: "País",
});

const ZIP_CODE_PATTERN = /^[A-Z0-9 -]+$/;

export function normalizarZipCode(value) {
  return String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9 -]/g, "");
}

export function paisEhBrasil(country) {
  return country === BRASIL;
}

export function normalizarCodigoPostal(value, country) {
  if (!paisEhBrasil(country)) return normalizarZipCode(value);

  const digits = String(value ?? "").replace(/[^0-9]/g, "").slice(0, 8);
  return digits.length > 5
    ? `${digits.slice(0, 5)}-${digits.slice(5)}`
    : digits;
}

export function rotuloCodigoPostal(country) {
  return paisEhBrasil(country) ? "CEP" : "ZIP Code";
}

export function cepBrasilValido(value) {
  const rawValue = String(value ?? "");
  return /^[0-9 -]+$/.test(rawValue) && rawValue.replace(/[^0-9]/g, "").length === 8;
}

export function validarEndereco(
  model = {},
  fieldMap = ADDRESS_FIELD_MAP,
  { optional = false } = {},
) {
  const fields = { ...ADDRESS_FIELD_MAP, ...fieldMap };
  const values = Object.fromEntries(
    Object.entries(fields).map(([key, field]) => [key, model?.[field]]),
  );
  const hasAnyValue = Object.values(values).some((value) => !isBlank(value));

  if (optional && !hasAnyValue) {
    return validResult();
  }

  if (isBlank(values.country)) {
    return invalidResult({ country: "Campo obrigatório." }, "País");
  }

  const errors = {};
  const brazilianAddress = paisEhBrasil(values.country);
  const rawPostalCode = String(values.postalCode ?? "");

  if (isBlank(rawPostalCode)) {
    errors.postalCode = "Campo obrigatório.";
  } else if (brazilianAddress) {
    if (!cepBrasilValido(rawPostalCode)) {
      errors.postalCode = "Informe um CEP brasileiro com 8 dígitos.";
    }
  } else if (
    !ZIP_CODE_PATTERN.test(rawPostalCode) ||
    !/[A-Z0-9]/.test(rawPostalCode)
  ) {
    errors.postalCode = "Informe um ZIP Code válido.";
  }

  REQUIRED_FIELDS.slice(1).forEach((field) => {
    if (isBlank(values[field])) {
      errors[field] = "Campo obrigatório.";
    }
  });

  const firstInvalidField = ["postalCode", ...REQUIRED_FIELDS.slice(1)].find(
    (field) => errors[field],
  );

  if (!firstInvalidField) {
    return validResult();
  }

  const label =
    firstInvalidField === "postalCode"
      ? rotuloCodigoPostal(values.country)
      : FIELD_LABELS[firstInvalidField];

  return invalidResult(errors, label);
}

function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

function validResult() {
  return { valid: true, errors: {}, firstInvalidLabel: "" };
}

function invalidResult(errors, firstInvalidLabel) {
  return { valid: false, errors, firstInvalidLabel };
}
